import { LEGACY_SERVER_UPDATED_AT, PLAYLIST_ORIGINS, PLAYLIST_SCHEMA_VERSION } from "./constants";

const isNumber = (v) => typeof v === "number" && isFinite(v);
const isInteger = (v) => isNumber(v) && Math.floor(v) === v;

/**
 * A stored playlist on disk:
 * { uid, id, name, urlEnc, epgUrlEnc?, is_selected?, fingerprint, schemaVersion, createdAt, updatedAt, origin,
 *   importedUpdatedAt?, status?, channels_count?, movies_count?, series_count?, expired_date? }
 *
 * Accepted only if every required field has the right type and a schema this build understands. Anything else
 * is "corrupt" for this build: counted and left exactly where it is, never repaired in place, never deleted.
 */
export const validateStoredPlaylist = (r) => {

    if (!r || typeof r !== "object") return null;

    if (typeof r.uid !== "string" || !r.uid) return null;
    if (!isInteger(r.id)) return null;
    if (typeof r.name !== "string") return null;
    if (typeof r.urlEnc !== "string" || !r.urlEnc) return null;
    if (typeof r.fingerprint !== "string" || !r.fingerprint) return null;
    if (!isNumber(r.createdAt) || !isNumber(r.updatedAt)) return null;
    if (!isNumber(r.schemaVersion) || r.schemaVersion > PLAYLIST_SCHEMA_VERSION) return null;

    const record = {
        uid: r.uid,
        id: r.id,
        name: r.name,
        urlEnc: r.urlEnc,
        fingerprint: r.fingerprint,
        schemaVersion: r.schemaVersion,
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
        origin: PLAYLIST_ORIGINS.indexOf(r.origin) !== -1 ? r.origin : "tv",
    };

    if (typeof r.epgUrlEnc === "string" && r.epgUrlEnc) record.epgUrlEnc = r.epgUrlEnc;
    if (typeof r.is_selected === "boolean") record.is_selected = r.is_selected;
    if (isNumber(r.importedUpdatedAt) && r.importedUpdatedAt > 0) record.importedUpdatedAt = r.importedUpdatedAt;
    if (typeof r.status === "string") record.status = r.status;
    if (isNumber(r.channels_count)) record.channels_count = r.channels_count;
    if (isNumber(r.movies_count)) record.movies_count = r.movies_count;
    if (isNumber(r.series_count)) record.series_count = r.series_count;
    if (typeof r.expired_date === "string") record.expired_date = r.expired_date;

    return record;

};

// A deletion: { uid, id, fingerprint, deletedAt }. No URL.
export const validateTombstone = (r) => {

    if (!r || typeof r !== "object") return null;
    if (typeof r.uid !== "string" || !r.uid) return null;
    if (!isInteger(r.id) || typeof r.fingerprint !== "string" || !isNumber(r.deletedAt)) return null;

    return { uid: r.uid, id: r.id, fingerprint: r.fingerprint, deletedAt: r.deletedAt };

};

/**
 * { schemaVersion, createdAt, legacyImportedAt?, PlaylistGeted? }
 * legacyImportedAt: the old `playlists` key has been imported and verified.
 * PlaylistGeted: the list register_device returned has been saved and verified; it is never read again.
 */
export const validateMeta = (r) => {

    if (!r || typeof r !== "object") return null;
    if (!isNumber(r.schemaVersion)) return null;

    const meta = {
        schemaVersion: r.schemaVersion,
        createdAt: isNumber(r.createdAt) ? r.createdAt : 0,
    };

    if (isNumber(r.legacyImportedAt)) meta.legacyImportedAt = r.legacyImportedAt;
    if (r.PlaylistGeted === true) meta.PlaylistGeted = true;

    return meta;

};

export const defaultMeta = (now) => ({ schemaVersion: PLAYLIST_SCHEMA_VERSION, createdAt: now });

/** What the rest of the app sees. The plaintext comes from the service's decrypted cache, never from here. */
export const toPlaylist = (record, plain) => {

    const playlist = {
        id: record.id,
        name: record.name,
        url: plain.url,
        is_selected: !!record.is_selected,
    };

    // a date of "never" (an import whose source had none) is left out rather than shown as 1970
    if (record.createdAt > 0) playlist.createdAt = new Date(record.createdAt).toISOString();

    const updatedAt = record.updatedAt > LEGACY_SERVER_UPDATED_AT ? record.updatedAt : record.importedUpdatedAt;
    if (updatedAt) playlist.updatedAt = new Date(updatedAt).toISOString();

    if (plain.epg) playlist.epg_url = plain.epg;
    if (record.status !== undefined) playlist.status = record.status;
    if (record.channels_count !== undefined) playlist.channels_count = record.channels_count;
    if (record.movies_count !== undefined) playlist.movies_count = record.movies_count;
    if (record.series_count !== undefined) playlist.series_count = record.series_count;
    if (record.expired_date !== undefined) playlist.expired_date = record.expired_date;

    return playlist;

};

/** At most one playlist is selected. If a merge produced two, the first (the target's) one wins. */
export const normalizeSelection = (records) => {

    let seen = false;

    return records.map((record) => {
        if (!record.is_selected) return record;
        if (!seen) {
            seen = true;
            return record;
        }
        return { ...record, is_selected: false };
    });

};
